/**
 * Owns the active ride session: start, finish (summary + FIT + save), crash recovery.
 */
import { db } from '@/db/db';
import { getSettings } from '@/store/settings';
import { useRide } from '@/store/ride';
import type { Course } from '@/routes/course';
import { FlatCourse } from '@/routes/course';
import type { RideMode } from '@/store/settingsModel';
import { encodeRideFit } from './fit';
import { summarize } from './metrics';
import type { ActiveRideSnapshot, RideRecord, RideSample, Lap } from './model';
import { RideSession, type SessionOptions, type VsHook, type WorkoutHook } from './session';

let current: RideSession | null = null;
let lastPublish = 0;

type FinishHook = (
  ride: RideRecord,
  samples: RideSample[],
  session: RideSession | null,
) => Promise<void> | void;
const finishHooks: FinishHook[] = [];

/** Other modules (planner, FTP tests, Strava) register post-ride processing here. */
export function onRideSaved(h: FinishHook): void {
  finishHooks.push(h);
}

export function getSession(): RideSession | null {
  return current;
}

/** Test helper: drop the in-memory session as a page reload would (the IndexedDB snapshot stays). */
export function simulateReloadForTests(): void {
  current?.stop();
  current = null;
}

export interface StartOptions {
  course?: Course;
  mode: RideMode;
  workout?: WorkoutHook | null;
  vs?: VsHook | null;
  draft?: () => number;
  plannedId?: string;
  resume?: ActiveRideSnapshot;
}

export function startRide(opts: StartOptions): RideSession {
  if (current && current.status !== 'finished') current.stop();
  const so: SessionOptions = { course: opts.course ?? new FlatCourse(), mode: opts.mode };
  if (opts.workout) so.workout = opts.workout;
  if (opts.vs) so.vs = opts.vs;
  if (opts.draft) so.draft = opts.draft;
  if (opts.plannedId) so.plannedId = opts.plannedId;
  if (opts.resume) so.resume = opts.resume;
  const s = new RideSession(so);
  current = s;
  s.onUpdate((sess) => {
    const now = performance.now();
    if (now - lastPublish >= 100 || sess.status !== 'running') {
      lastPublish = now;
      useRide.getState().setView(sess.view());
    }
  });
  s.start();
  useRide.getState().setView(s.view());
  void s.persist();
  return s;
}

function rideName(
  courseName: string | undefined,
  workoutName: string | undefined,
  startedAt: number,
): string {
  const d = new Date(startedAt);
  const hour = d.getHours();
  const part = hour < 11 ? 'Morgen' : hour < 14 ? 'Mittag' : hour < 18 ? 'Nachmittag' : 'Abend';
  if (workoutName) return `${workoutName} (VeloStube)`;
  if (courseName && courseName !== 'Flach') return `${courseName} – ${part}sfahrt`;
  return `VeloStube ${part}sfahrt`;
}

export async function saveRide(
  base: {
    id: string;
    startedAt: number;
    samples: RideSample[];
    laps: Lap[];
    routeId?: string;
    routeName?: string;
    workoutId?: string;
    workoutName?: string;
    plannedId?: string;
  },
  session: RideSession | null,
): Promise<RideRecord> {
  const settings = getSettings();
  const samples = base.samples;
  const endedAt = samples[samples.length - 1]?.t ?? Date.now();
  const summary = summarize(samples, settings.ftp, Math.round((endedAt - base.startedAt) / 1000));
  const ride: RideRecord = {
    id: base.id,
    name: rideName(base.routeName, base.workoutName, base.startedAt),
    startedAt: base.startedAt,
    endedAt,
    laps: base.laps,
    summary,
    strava: { state: 'none' },
    updatedAt: Date.now(),
  };
  if (base.routeId) ride.routeId = base.routeId;
  if (base.routeName) ride.routeName = base.routeName;
  if (base.workoutId) ride.workoutId = base.workoutId;
  if (base.workoutName) ride.workoutName = base.workoutName;
  if (base.plannedId) ride.plannedId = base.plannedId;
  for (const h of finishHooks) {
    try {
      await h(ride, samples, session);
    } catch (err) {
      console.warn('ride hook failed', err);
    }
  }
  const fit = encodeRideFit(ride, samples);
  await db.transaction('rw', db.rides, db.rideData, db.activeRide, async () => {
    await db.rides.put(ride);
    await db.rideData.put({ rideId: ride.id, samples, fit });
    await db.activeRide.delete('active');
  });
  return ride;
}

/** Finish the active ride and save it. Returns the saved record. */
export async function finishRide(): Promise<RideRecord | null> {
  const s = current;
  if (!s) return null;
  s.stop();
  current = null;
  useRide.getState().setView(null);
  const base: Parameters<typeof saveRide>[0] = {
    id: s.id,
    startedAt: s.startedAt,
    samples: s.recorder.samples,
    laps: s.recorder.laps,
  };
  if (s.course.route) {
    base.routeId = s.course.id;
    base.routeName = s.course.name;
  }
  if (s.workout) {
    base.workoutId = s.workout.workout.id;
    base.workoutName = s.workout.workout.name;
  }
  if (s.plannedId) base.plannedId = s.plannedId;
  return saveRide(base, s);
}

/** Abort without saving. */
export async function discardRide(): Promise<void> {
  current?.stop();
  current = null;
  useRide.getState().setView(null);
  await db.activeRide.delete('active');
}

// ---- crash recovery (SPEC §10.1) ----

export async function getRecoverable(): Promise<ActiveRideSnapshot | null> {
  if (current) return null;
  const snap = await db.activeRide.get('active');
  if (!snap || snap.samples.length < 5) return null;
  return snap;
}

export async function saveRecovered(snap: ActiveRideSnapshot): Promise<RideRecord> {
  const base: Parameters<typeof saveRide>[0] = {
    id: snap.rideId,
    startedAt: snap.startedAt,
    samples: snap.samples,
    laps: snap.laps.length ? snap.laps : [],
  };
  if (snap.routeId) {
    base.routeId = snap.routeId;
    const r = await db.routes.get(snap.routeId);
    if (r) base.routeName = r.meta.name;
  }
  if (snap.workoutId) {
    base.workoutId = snap.workoutId;
    const w = await db.workouts.get(snap.workoutId);
    if (w) base.workoutName = w.name;
  }
  if (snap.plannedId) base.plannedId = snap.plannedId;
  const last = snap.samples[snap.samples.length - 1]!;
  const firstLapStart = snap.laps[snap.laps.length - 1];
  base.laps = [
    ...snap.laps,
    {
      startT: firstLapStart?.endT ?? snap.startedAt,
      endT: last.t,
      startDist: firstLapStart?.endDist ?? 0,
      endDist: last.dist,
      startEl: firstLapStart?.endEl ?? 0,
      endEl: last.el,
      trigger: 'session_end',
    },
  ];
  return saveRide(base, null);
}

export async function discardRecovered(): Promise<void> {
  await db.activeRide.delete('active');
}
